import { Autocomplete, Chip, TextField } from '@mui/material';
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CostCenter } from '~/data/costCenters';
import { Form } from '~/features/RehireDialog/hooks/useRehireEmpForm';
import { useSxStyles } from '~/features/RehireDialog/useStyles';

type Props = {
  control: Control<Form>;
  getValues: UseFormGetValues<Form>;
  setValue: UseFormSetValue<Form>;
  costCenterValues: CostCenter[];
};

export const CostCenterComponents = ({
  control,
  costCenterValues,
  getValues,
  setValue,
}: Props) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const [fInternalPositionUnitValue, fEmpCostCenter] = useWatch({
    control,
    name: [
      'rehire.empPosition.fInternalPositionUnit',
      'rehire.empPosition.fEmpCostCenter',
    ],
  });

  return (
    <>
      <Controller
        control={control}
        name={'rehire.empPosition.fEmpCostCenter'}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Autocomplete
            sx={{ ...sxStyles.selectStyle, ...sxStyles.inputBottomMargin }}
            placeholder={
              fInternalPositionUnitValue === -1
                ? t('laSelectFirstOrgUnit')
                : ' '
            }
            options={costCenterValues}
            getOptionLabel={(c) => `${c.fCostCenterCode}, ${c.fCostCenterName}`}
            isOptionEqualToValue={(o, v) =>
              o?.fCostCenterId === v?.fCostCenterId
            }
            value={
              costCenterValues.find(
                (center) => center?.fCostCenterId?.toString() === value,
              ) || null
            }
            onChange={(e, value) => {
              onChange(value?.fCostCenterId?.toString());
              const additionalCC = getValues(
                'rehire.empPosition.fEmpAddlCostCenters',
              );
              if (additionalCC?.length) {
                setValue(
                  'rehire.empPosition.fEmpAddlCostCenters',
                  (additionalCC as CostCenter[]).filter(
                    (cc) => cc.fCostCenterId !== value?.fCostCenterId,
                  ),
                );
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label={t('fEmpCostCenter')}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name={'rehire.empPosition.fEmpAddlCostCenters'}
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            sx={{ ...sxStyles.selectStyle, ...sxStyles.inputBottomMargin }}
            placeholder={' '}
            options={costCenterValues}
            getOptionLabel={(c) => `${c.fCostCenterCode}, ${c.fCostCenterName}`}
            isOptionEqualToValue={(o, v) =>
              o?.fCostCenterId === v?.fCostCenterId
            }
            multiple
            value={value as CostCenter[]}
            onChange={(e, value) => {
              onChange(value);
            }}
            getOptionDisabled={(option) =>
              `${option.fCostCenterId}` === fEmpCostCenter
            }
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  label={`${option.fCostCenterCode}, ${option.fCostCenterName}`}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label={t('fEmpAddlCostCenters')} />
            )}
          />
        )}
      />
    </>
  );
};
